import {
  FileType2,
  FileCode2,
  FileBadge,
  FilePlus2,
  FileLock2,
  HandCoins,
  Briefcase,
  ServerOff,
  ClipboardType,
  FileCheck,
  FileUp,
} from 'lucide-react';

import { LiabilityEnum, LegalContextEnum, AgreementConditionEnum, JurisdictionEnum } from 'src/graphql/schema';

const agreementTemplateOptionsRich = [
  {
    value: 'false',
    label: 'Standard template',
    icon: FileCheck,
    description:
      'Use our tried and tested, standard template to generate a tailored escrow agreement based on your selected configurations.',
  },
  {
    value: 'true',
    label: 'External escrow agreement template',
    icon: FileUp,
    description:
      'Use your own template agreement. To suggest these terms, email the document in .docx format to service@codekeeper.co',
  },
];

const agreementTemplateOptionsRichDictionary = agreementTemplateOptionsRich.reduce(
  (acc, item) => {
    acc[item.value] = item;
    return acc;
  },
  {} as Record<string, (typeof agreementTemplateOptionsRich)[0]>,
);

const legalContextOptionsRich = [
  {
    title: 'License Agreement',
    value: LegalContextEnum.License,
    description: 'A contract where the depositor acts as a software provider and the beneficiary as a licensee.',
    icon: FileBadge,
  },
  {
    title: 'Development Agreement',
    value: LegalContextEnum.Development,
    description: 'A contract defining the conditions under which software is being developed for the beneficiary.',
    icon: FileCode2,
  },
  {
    title: 'Transaction Agreement',
    value: LegalContextEnum.Transaction,
    description: 'A contract governing the transfer of rights or ownership as part of a sale or purchase.',
    icon: FilePlus2,
  },
  {
    title: 'SaaS Agreement',
    value: LegalContextEnum.Saas,
    description:
      'A contract outlining conditions for the usage, support and payment related to cloud-based software being provided as a service.',
    icon: FileLock2,
  },
  {
    title: 'Other',
    value: LegalContextEnum.Other,
    description: 'Any other service agreements or contract types not listed here.',
    icon: FileType2,
  },
];

const legalContextMap = legalContextOptionsRich.reduce(
  (acc, item) => {
    acc[item.value] = item;
    return acc;
  },
  {} as Record<string, (typeof legalContextOptionsRich)[0]>,
);

const releaseEventsOptionsRich = [
  {
    title: 'Bankruptcy',
    value: AgreementConditionEnum.Bankruptcy,
    description: 'The depositor applies for bankruptcy, or ceases to exist as an entity.',
    icon: HandCoins,
  },
  {
    title: 'Insolvency',
    value: AgreementConditionEnum.Insolvency,
    description:
      'The depositor is declared insolvent, or demonstrated not being able to meet their financial requirements.',
    icon: Briefcase,
  },
  {
    title: 'Maintenance obligations not met',
    value: AgreementConditionEnum.Maintenance,
    description:
      'The depositor has discontinued, or cannot continue maintenance of the application, and cannot transfer this to an entity the beneficiary can accept.',
    icon: ServerOff,
  },
  {
    title: 'Other',
    value: AgreementConditionEnum.Other,
    description: 'Any other event not listed here.',
    icon: ClipboardType,
  },
];

const releaseEventsMap = releaseEventsOptionsRich.reduce(
  (acc, item) => {
    acc[item.value] = item;
    return acc;
  },
  {} as Record<string, (typeof releaseEventsOptionsRich)[0]>,
);

const jurisdictionOptions = [
  {
    label: 'Escrow Agent’s Jurisdiction',
    value: JurisdictionEnum.EscrowAgentJurisdiction,
  },
  {
    label: 'Depositor’s Jurisdiction',
    value: JurisdictionEnum.DepositorJurisdiction,
  },
  {
    label: 'Beneficiary’s Jurisdiction',
    value: JurisdictionEnum.BeneficiaryJurisdiction,
  },
];

const jurisdictionMap = jurisdictionOptions.reduce(
  (acc, item) => {
    acc[item.value] = item.label;
    return acc;
  },
  {} as Record<JurisdictionEnum, string>,
);

const liabilityMap = {
  [LiabilityEnum.Total_0]: 'Standard limit',
  [LiabilityEnum.Total_29]: '$50,000 total aggregate',
  [LiabilityEnum.Total_89]: '$100,000 total aggregate',
  [LiabilityEnum.Total_149]: '$250,000 total aggregate',
};

export {
  legalContextOptionsRich,
  legalContextMap,
  releaseEventsOptionsRich,
  releaseEventsMap,
  jurisdictionOptions,
  jurisdictionMap,
  liabilityMap,
  agreementTemplateOptionsRichDictionary,
  agreementTemplateOptionsRich,
};
